/*
 * Analog Theme
 *
 * Use this file to add custom Alpine.js code to the Analog Theme. Keeping your
 * custom Alpine in this file makes it easier to update Analog. The import for
 * this file is enabled in snippets/head-scripts.liquid.
 */

import Alpine from 'alpinejs';

/*
 * ISO 3166-1 alpha-2 codes for every country in Europe or Asia. Visitors
 * detected in one of these regions get a minimum drinking age of 18; everyone
 * else falls back to the section's configured default age (21). Baked in so the
 * age gate has zero third-party network dependencies — the only lookup is
 * Shopify's own (same-origin) browsing_context_suggestions endpoint.
 */
const AGE_18_COUNTRIES = new Set([
  // Europe
  'AL', 'AD', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK',
  'EE', 'ES', 'FI', 'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM',
  'IS', 'IT', 'JE', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL',
  'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SJ', 'SK', 'SM', 'UA', 'VA',
  'XK',
  // Asia
  'AF', 'AE', 'AM', 'AZ', 'BD', 'BH', 'BN', 'BT', 'CN', 'GE', 'HK', 'ID', 'IL',
  'IN', 'IQ', 'IR', 'JO', 'JP', 'KG', 'KH', 'KP', 'KR', 'KW', 'KZ', 'LA', 'LB',
  'LK', 'MM', 'MN', 'MO', 'MV', 'MY', 'NP', 'OM', 'PH', 'PK', 'PS', 'QA', 'SA',
  'SG', 'SY', 'TH', 'TJ', 'TL', 'TM', 'TR', 'TW', 'UZ', 'VN', 'YE',
]);

// Registrations must happen inside `alpine:init` so they exist when Alpine boots.
document.addEventListener('alpine:init', () => {
  /*
   * Geo store — ONE same-origin country lookup, shared by the IP-based country-links
   * blocks (custom-country-links + custom-country). Uses Shopify's own
   * browsing_context_suggestions.json (same source as the age gate — no third-party geo
   * API). `available` collects the country codes that have a block on the page so the
   * parent can show its fallback when the visitor's country isn't covered. In the theme
   * editor it resolves immediately (designMode) so every country block is visible to edit.
   */
  Alpine.store('geo', {
    country: '',
    ready: false,
    designMode: false,
    available: [],

    register(code) {
      code = (code || '').toUpperCase();
      if (code && !this.available.includes(code)) this.available.push(code);
    },

    async init() {
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      if (this.designMode) {
        this.ready = true;
        return;
      }
      const shop = window.Shopify || {};
      try {
        const params =
          'country[enabled]=true&country[exclude]=' + (shop.country || '') +
          '&language[enabled]=true&language[exclude]=' + (shop.language || '');
        const root = (shop.routes && shop.routes.root) || '/';
        const res = await fetch(root + 'browsing_context_suggestions.json?' + params);
        const handle = (await res.json())?.detected_values?.country?.handle;
        this.country = (handle || shop.country || '').toUpperCase();
      } catch (e) {
        this.country = (shop.country || '').toUpperCase();
        console.warn('geo store: country lookup failed', e);
      } finally {
        this.ready = true;
      }
    },
  });

  /*
   * age-verification-gate section. Pairs with sections/age-verification-gate.liquid.
   * Markup supplies the x-dialog.modal shell; this component owns the state:
   * region-aware minimum age, the four birth-year digits, validation, and the
   * persisted "already verified" flag.
   */
  Alpine.data('ageGate', (opts = {}) => ({
    designMode: opts.designMode || false,
    enableGeo: opts.enableGeo || false,
    defaultAge: opts.defaultAge || 21,
    errorTemplate: opts.errorTemplate || 'You need to be at least age_limit years old to enter.',

    editorOpen: false,
    verified: Alpine.$persist(false).as('no3-age-verified'),
    minAge: opts.defaultAge || 21,
    showError: false,
    digits: ['', '', '', ''],

    get errorMessage() {
      return this.errorTemplate.replaceAll('age_limit', this.minAge);
    },

    // Storefront: open until verified (hard gate). Theme editor: open only when
    // the section is selected, so the merchant can edit without being trapped.
    get gateOpen() {
      return this.designMode ? this.editorOpen : !this.verified;
    },

    // Config handed to x-dialog.modal. The inner getter/setter close over the
    // component instance (`self`) rather than relying on Alpine's with-scope to
    // resolve bare identifiers — that fails for Alpine.data() components. Keeps
    // the gate hard: only a successful age check (which flips `verified`) closes
    // it on the storefront; escape/backdrop/native-close are no-ops.
    get dialogConfig() {
      const self = this;
      return {
        get isOpen() {
          return self.gateOpen;
        },
        set isOpen(value) {
          if (self.designMode) self.editorOpen = value;
        },
      };
    },

    async init() {
      // Theme editor: a settings change re-renders the section, which re-inits
      // this component and resets editorOpen. The editor's selected-section
      // store persists across that re-render, so re-derive open state from it —
      // the gate re-opens itself if this section is still selected. ($store-based
      // drawers like header-drawer survive re-render for the same reason: their
      // open state lives outside the section.)
      if (this.designMode) {
        this.editorOpen = this.$isSelectedSection;
      }

      if (!this.enableGeo || this.designMode || this.verified) return;
      try {
        const params =
          'country[enabled]=true&country[exclude]=' + window.Shopify.country +
          '&language[enabled]=true&language[exclude]=' + window.Shopify.language;
        const res = await fetch(window.Shopify.routes.root + 'browsing_context_suggestions.json?' + params);
        const handle = (await res.json())?.detected_values?.country?.handle;
        const code = (handle || window.Shopify.country || '').toUpperCase();
        this.minAge = AGE_18_COUNTRIES.has(code) ? 18 : this.defaultAge;
      } catch (e) {
        this.minAge = this.defaultAge;
        console.warn('Age gate: region lookup failed, using default age', this.minAge, e);
      }
    },

    // `digits` is the source of truth (not the DOM) — robust against the dialog
    // morphing/duplicating its content. Each input is :value-bound to digits[idx].
    onInput(e, idx) {
      this.showError = false;
      let v = (e.target.value || '').replace(/\D/g, '');
      if (v.length > 1) v = v.slice(-1);
      this.digits[idx] = v;
      e.target.value = v;
      if (v && idx < this.digits.length - 1) e.target.nextElementSibling?.focus();
    },

    onKeydown(e, idx) {
      if (e.key === 'Backspace' && !e.target.value && idx > 0) {
        e.target.previousElementSibling?.focus();
      }
      if (e.key === 'Enter') this.validate();
    },

    validate() {
      const year = this.digits.join('');
      if (year.length < this.digits.length || this.digits.some((d) => d === '')) {
        this.showError = true;
        return;
      }
      const age = new Date().getFullYear() - parseInt(year, 10);
      if (age >= this.minAge) {
        this.verified = true;
        this.showError = false;
      } else {
        this.showError = true;
      }
    },
  }));

  /*
   * menu-button section. Pairs with sections/menu-button.liquid. Owns the
   * float-up/snap scroll motion of the floating "MENU" pill — there is no Analog
   * equivalent (x-header-stack only governs the header element's own sticky).
   *
   * On pages WITH a hero the pill starts pinned near the bottom of the viewport
   * and parallaxes upward as you scroll, then snaps sticky to the top once it
   * reaches the snap threshold (desktop snaps as soon as the hero leaves; mobile
   * snaps once the pill itself reaches the top). On pages WITHOUT a hero it is
   * sticky at the top from load. Config arrives via data-* attributes (read in
   * init) to avoid the `| json` Alpine-attribute quoting trap. The overlay is a
   * sibling x-dialog.modal — this component does not manage it.
   */
  Alpine.data('menuButton', () => ({
    open: false,
    isSticky: false,
    scrollY: 0,
    hasHero: false,

    floatEnabled: true,
    snapOffset: 10,
    bottomOffset: 25,

    _onScroll: null,
    _onResize: null,
    _heroEl: null,
    _anchorEl: null,
    _threshold: 0,
    _openOnStick: false,
    _lastWidth: 0,

    init() {
      const d = this.$el.dataset;
      this.floatEnabled = d.floatEnabled !== 'false';
      this.snapOffset = Number.isNaN(parseInt(d.snapOffset, 10)) ? 10 : parseInt(d.snapOffset, 10);
      this.bottomOffset = Number.isNaN(parseInt(d.bottomOffset, 10)) ? 25 : parseInt(d.bottomOffset, 10);

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // [data-allows-overlay-header] is Analog's transparent-hero hook; [data-hero-image]
      // is the bottle hook the custom hero (section #2) will expose.
      this._heroEl = document.querySelector('[data-allows-overlay-header], [data-hero-image]');
      // Prefer the hero's content-bottom marker (the agreed stick point) so the button
      // sticks at the visible-content bottom even when the banner adds extra scroll space
      // below it; fall back to the banner section itself.
      this._anchorEl = document.querySelector('[data-hero-anchor]') || this._heroEl;
      this.hasHero = this.floatEnabled && !reduce && !!this._heroEl;

      if (!this.hasHero) {
        this.isSticky = true;
        return;
      }

      this._lastWidth = window.innerWidth;
      this.measure();
      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.update();
          ticking = false;
        });
      };
      // Only re-measure on WIDTH changes (orientation / real resize). Mobile toolbars
      // fire resize with a height-only change — ignoring those keeps the anchor (and
      // therefore the button) from shifting while you scroll.
      this._onResize = () => {
        if (window.innerWidth !== this._lastWidth) {
          this._lastWidth = window.innerWidth;
          this.measure();
        }
        this.update();
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      this.update();
    },

    // Anchor the floating button to the BANNER's bottom (a document position), not the
    // viewport bottom — so the mobile toolbar showing/hiding doesn't move it. `_threshold`
    // is the scrollY at which the pill's top edge reaches the snap offset; it's measured
    // on load and width-changes only, never per scroll frame.
    measure() {
      const btn = this.$refs.pillButton;
      const h = btn ? btn.offsetHeight : 70;
      let anchorY = window.innerHeight; // fallback if the banner can't be measured
      if (this._anchorEl) {
        anchorY = this._anchorEl.getBoundingClientRect().bottom + window.scrollY;
      }
      this._threshold = Math.max(0, anchorY - this.bottomOffset - h - this.snapOffset);
    },

    update() {
      this.scrollY = window.scrollY;
      this.isSticky = this.scrollY >= this._threshold;

      // When floating, opening waits until we've scrolled the pill up to the top so the
      // accordion only ever expands from a single, top-anchored button (never two).
      if (this._openOnStick && this.isSticky) {
        this._openOnStick = false;
        this.open = true;
      }
    },

    // Toggle behaves like an accordion. Closed + floating in a hero → first scroll the
    // pill up so it sticks at the top, then open (avoids a second button at the bottom).
    // Closed + already sticky (or no hero) → open immediately. Open → close.
    toggle() {
      if (this.open) {
        this.open = false;
        return;
      }
      if (!this.hasHero || this.isSticky) {
        this.open = true;
        return;
      }
      this._openOnStick = true;
      window.scrollTo({ top: this._threshold, behavior: 'smooth' });
    },

    // Positioned entirely via `top` (NO transform) so the pill — which carries the
    // glass blur — never sits under a transform that would kill its backdrop-filter, and
    // so the float→stick transition is one continuous value (no jump). Centered with
    // margin:auto. Floating top rises 1:1 with scroll and meets the snap offset exactly
    // at `_threshold`.
    get pillStyle() {
      // Width (fit-content) lives in CSS so it can carry the -webkit- prefix iOS Safari
      // needs — without it Safari falls back to width:auto and the pill goes full-width.
      const base = 'position:fixed; left:0; right:0; margin-inline:auto; z-index:60;';
      if (!this.hasHero || this.isSticky) {
        return `${base} top:${this.snapOffset}px;`;
      }
      const top = this._threshold + this.snapOffset - this.scrollY;
      return `${base} top:${top}px;`;
    },

    destroy() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    },
  }));

  /*
   * parallax-banner section. Pairs with sections/parallax-banner.liquid. Owns the
   * fixed background (solid colour + centred SVG, x-ref="bg") and its scroll-driven
   * blur. The background is position:fixed (covers the viewport); the foreground
   * @theme blocks scroll over it.
   *  - Release: while the section fills the viewport the background holds still; once
   *    the section's bottom rises into view it slides up by the same amount so it
   *    leaves WITH the section bottom (no bleed over the next section).
   *  - Blur: 0 until the content-bottom marker ([data-hero-anchor], the menu button's
   *    stick point) reaches the top, then scrubs 0 → max over the remaining
   *    (extra-space) scroll.
   */
  Alpine.data('parallaxBanner', () => ({
    _blur: false,
    _blurMax: 0,
    _bg: null,
    _marker: null,
    _onScroll: null,

    init() {
      const d = this.$el.dataset;
      this._blur = d.blur === 'true';
      this._blurMax = parseFloat(d.blurMax) || 0;
      this._bg = this.$refs.bg;
      this._marker = this.$el.querySelector('[data-hero-anchor]');

      // Reduced motion: drop the fixed parallax + blur. Anchor the background to the
      // first viewport so it scrolls away normally with the content.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (this._bg) {
          this._bg.style.position = 'absolute';
          this._bg.style.height = '100svh';
        }
        return;
      }

      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.apply();
          ticking = false;
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this.apply();
    },

    apply() {
      if (!this._bg) return;
      const rect = this.$el.getBoundingClientRect();
      const vh = window.innerHeight;

      // Release — the background is fixed (covers the viewport). Hold it pinned while the
      // section fills the viewport; once the section's bottom rises into view, slide it up
      // by (viewport − sectionBottom) so its bottom edge tracks the section bottom and it
      // scrolls away cleanly. translate3d keeps it on its own GPU layer.
      const releaseShift = Math.max(0, vh - rect.bottom);
      this._bg.style.transform = `translate3d(0, ${-releaseShift}px, 0)`;

      // Blur — begins where the marker sits (the button's stick point) and ramps over the
      // remaining scroll. Marker offset from the section top is constant while scrolling.
      if (this._blur) {
        const scrolledIn = Math.max(0, -rect.top);
        const blurStart = this._marker
          ? this._marker.getBoundingClientRect().top - rect.top
          : vh;
        // Ramp over the scroll that REMAINS after the marker reaches the top — i.e. the
        // section's scrollable distance (height − viewport) minus the marker offset — so
        // blur hits max exactly as the section finishes scrolling (and starts releasing).
        const blurRange = Math.max(1, rect.height - vh - blurStart);
        const progress = Math.min(1, Math.max(0, (scrolledIn - blurStart) / blurRange));
        this._bg.style.filter = `blur(${(progress * this._blurMax).toFixed(2)}px)`;
      }
    },

    destroy() {
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);
      }
    },
  }));

  /*
   * sticky-text-rows section. Pairs with sections/sticky-text-rows.liquid. Owns the
   * "wheel": a left rail of numbered key indicators whose ACTIVE item is always pinned
   * to the vertical centre of the viewport, plus the cosine fade/scale of indicators and
   * the fade of the tall text rows. One rAF scroll pass derives a continuous active index
   * from the row centres and drives everything from it, so rail + text stay in sync. The
   * same pass applies a gentle per-row icon parallax. No $m / Motion magic needed —
   * x-parallax:element needs a parent x-parallax driver, so the icon drift is done here in
   * the existing scroll pass instead (simpler, and stays in sync with the wheel).
   */
  Alpine.data('stickyRows', () => ({
    _spread: 1.2,
    _stack: null,
    _indicators: [],
    _rows: [],
    _titles: [],
    _icons: [],
    _pitch: 0,
    _mid: 0,
    _reduce: false,
    _onScroll: null,
    _onResize: null,

    init() {
      const d = this.$el.dataset;
      this._spread = parseFloat(d.spread) || 1.2;
      this._reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._stack = this.$refs.railStack;
      this._indicators = Array.from(this.$el.querySelectorAll('[data-sr-indicator]'));
      this._rows = Array.from(this.$el.querySelectorAll('[data-sr-row]'));
      this._titles = this._rows.map((r) => r.querySelector('[data-sr-title]'));
      this._icons = this._rows.map((r) => r.querySelector('[data-sr-icon]'));
      this._mid = (this._rows.length - 1) / 2;

      // Reduced motion: leave everything at its natural full-opacity, centred-stack state.
      if (this._reduce) return;

      this.measure();
      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.apply();
          ticking = false;
        });
      };
      this._onResize = () => {
        this.measure();
        this.apply();
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      this.apply();
    },

    // Pitch = vertical distance between consecutive indicator centres (item height + gap).
    // Measured here (and on resize) so the wheel translate doesn't read layout every frame.
    measure() {
      if (this._indicators.length >= 2) {
        this._pitch =
          this._indicators[1].getBoundingClientRect().top -
          this._indicators[0].getBoundingClientRect().top;
      } else {
        this._pitch = 0;
      }
    },

    // Smooth cosine drop from 1 (at the centre) to 0 at `radius` index-units away.
    falloff(distance) {
      const r = this._spread;
      if (distance >= r) return 0;
      return 0.5 * (1 + Math.cos(Math.PI * (distance / r)));
    },

    apply() {
      if (!this._rows.length) return;
      const vh = window.innerHeight;
      // Focus line sits a little ABOVE the exact centre so the active row + rail read a touch
      // higher in the viewport (dead-centre felt low). Bump FOCUS_OFFSET to raise it further.
      const FOCUS_OFFSET = vh * 0.08;
      const centerY = vh / 2 - FOCUS_OFFSET;

      // Continuous active index from the row centres. Rows are stacked min-h-screen, so the
      // pitch between row centres is ~constant; interpolate where the viewport centre falls.
      const c0 = this._rows[0].getBoundingClientRect();
      const firstCenter = c0.top + c0.height / 2;
      let rowPitch = vh;
      if (this._rows.length >= 2) {
        const c1 = this._rows[1].getBoundingClientRect();
        rowPitch = c1.top + c1.height / 2 - firstCenter;
      }
      let activeF = rowPitch ? (centerY - firstCenter) / rowPitch : 0;
      activeF = Math.min(this._rows.length - 1, Math.max(0, activeF));

      // WHEEL — slide the rail stack so item `activeF` lands on the focus line. Base layout
      // centres the middle item (flex justify-center) at vh/2, so we offset from there by the
      // index delta AND the same FOCUS_OFFSET, keeping the active indicator level with the text.
      if (this._stack && this._pitch) {
        const ty = (this._mid - activeF) * this._pitch - FOCUS_OFFSET;
        this._stack.style.transform = `translate3d(0, ${ty.toFixed(2)}px, 0)`;
      }

      // FADE/SCALE (keyed off |index − activeF| so the centred item peaks at 1) plus a
      // gentle per-row ICON parallax (drifts opposite to its distance from centre).
      for (let i = 0; i < this._rows.length; i++) {
        const influence = this.falloff(Math.abs(i - activeF));
        const scale = 1 + 0.2 * influence;
        const ind = this._indicators[i];
        if (ind) {
          const num = ind.querySelector('.sr-number');
          const key = ind.querySelector('.sr-key');
          if (num) num.style.opacity = influence;
          if (key) key.style.opacity = (0.1 + 0.9 * influence).toFixed(3);
          ind.style.transform = `scale(${scale.toFixed(3)})`;
        }
        const title = this._titles[i];
        if (title) title.style.opacity = influence;

        const icon = this._icons[i];
        if (icon) {
          const rr = this._rows[i].getBoundingClientRect();
          const rowCenter = rr.top + rr.height / 2;
          const drift = (centerY - rowCenter) * 0.12; // ±~one-icon over a viewport of scroll
          icon.style.transform = `translate3d(0, ${drift.toFixed(2)}px, 0)`;
        }
      }
    },

    destroy() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    },
  }));
});
